import { CountryRegionData } from "react-country-region-selector";

const COUNTRY_RECORDS = CountryRegionData.map(([countryName, countryCode, regionList]) => ({
  countryName,
  countryCode,
  regions: (regionList || "")
    .split("|")
    .filter(Boolean)
    .map((regionPair) => {
      const [regionName, regionShortCode = ""] = regionPair.split("~");

      return {
        regionName,
        regionShortCode,
      };
    }),
}));

const COUNTRY_CODE_BY_NAME = new Map(COUNTRY_RECORDS.map(({ countryName, countryCode }) => [countryName, countryCode]));
const COUNTRY_NAME_BY_CODE = new Map(COUNTRY_RECORDS.map(({ countryName, countryCode }) => [countryCode, countryName]));
const REGIONS_BY_COUNTRY = new Map(COUNTRY_RECORDS.map(({ countryName, regions }) => [countryName, regions]));

export function getAddressProfileDefaults(details = null) {
  return {
    country: details?.country || "Canada",
    firstName: details?.firstName || "",
    lastName: details?.lastName || "",
    address: details?.address || "",
    addressLine2: details?.addressLine2 || "",
    city: details?.city || "",
    state: details?.state || "",
    postalCode: details?.postalCode || "",
    phoneNumber: details?.phoneNumber || "",
  };
}

export function formatPhoneValue(rawValue = "") {
  const phone = String(rawValue).replace(/\D/g, "");

  let formattedPhone = "";
  if (phone.length > 3) {
    formattedPhone += `${phone.slice(0, 3)}-`;
    if (phone.length > 6) {
      formattedPhone += `${phone.slice(3, 6)}-${phone.slice(6, 10)}`;
    } else {
      formattedPhone += phone.slice(3);
    }
  } else {
    formattedPhone = phone;
  }

  return formattedPhone;
}

export function getCountryCodeFromName(countryName) {
  if (!countryName) {
    return "";
  }

  return COUNTRY_CODE_BY_NAME.get(countryName) || "";
}

export function getCountryNameFromCode(countryCode) {
  if (!countryCode) {
    return "";
  }

  return COUNTRY_NAME_BY_CODE.get(String(countryCode).toUpperCase()) || "";
}

export function getRegionNameFromCode(countryName, regionCode) {
  if (!countryName || !regionCode) {
    return "";
  }

  const normalizedRegionCode = String(regionCode).toUpperCase();
  const regions = REGIONS_BY_COUNTRY.get(countryName) || [];
  const matchedRegion = regions.find(({ regionShortCode, regionName }) => {
    return regionShortCode.toUpperCase() === normalizedRegionCode || regionName.toUpperCase() === normalizedRegionCode;
  });

  return matchedRegion?.regionName || "";
}

function getAddressComponentMap(addressComponents = []) {
  return addressComponents.reduce((componentMap, component) => {
    (component?.types || []).forEach((type) => {
      if (!componentMap.has(type)) {
        componentMap.set(type, component);
      }
    });

    return componentMap;
  }, new Map());
}

function getComponentText(componentMap, type, textType = "longText") {
  const component = componentMap.get(type);
  if (!component) {
    return "";
  }

  if (textType === "longText") {
    return component.longText || component.long_name || "";
  }

  if (textType === "shortText") {
    return component.shortText || component.short_name || "";
  }

  return component[textType] || "";
}

export function normalizePlaceAddress(placeOrAddressComponents) {
  const addressComponents = Array.isArray(placeOrAddressComponents)
    ? placeOrAddressComponents
    : placeOrAddressComponents?.addressComponents || [];
  const componentMap = getAddressComponentMap(addressComponents);

  const countryCode = getComponentText(componentMap, "country", "shortText");
  const country = getCountryNameFromCode(countryCode) || getComponentText(componentMap, "country");
  const streetNumber = getComponentText(componentMap, "street_number");
  const route = getComponentText(componentMap, "route");
  const premise = getComponentText(componentMap, "premise");
  const city =
    getComponentText(componentMap, "locality") ||
    getComponentText(componentMap, "postal_town") ||
    getComponentText(componentMap, "sublocality_level_1") ||
    getComponentText(componentMap, "administrative_area_level_3");
  const stateCode = getComponentText(componentMap, "administrative_area_level_1", "shortText");
  const state =
    getRegionNameFromCode(country, stateCode) ||
    getComponentText(componentMap, "administrative_area_level_1") ||
    stateCode;
  const postalCode = getComponentText(componentMap, "postal_code");
  const postalCodeSuffix = getComponentText(componentMap, "postal_code_suffix");

  return {
    address: [streetNumber, route].filter(Boolean).join(" ").trim() || premise || route,
    city,
    state,
    postalCode: postalCode && postalCodeSuffix ? `${postalCode}-${postalCodeSuffix}` : postalCode,
    country,
  };
}

export function getSuggestionLabel(suggestion) {
  const predictionText = suggestion?.placePrediction?.text;

  if (typeof predictionText?.toString === "function") {
    const text = predictionText.toString();
    if (text) {
      return text;
    }
  }

  return predictionText?.text || "";
}
